import React, { useState } from 'react';
import { Loader, X } from 'react-feather';
import { useParams } from 'react-router-dom';

import {
  EditShippingAddressModalProps,
  UpdateShippingAddressRequest,
} from '@/Order/types/orderTypes';
import { useApiMutation } from '@/shared/hooks/useApi';
import { api } from '@/shared/services/api';
import { extractErrorMessage } from '@/shared/utils/errorUtils';

const EditShippingAddressModal: React.FC<EditShippingAddressModalProps> = ({
  receiverInfo,
  onSuccess,
  onClose,
}) => {
  const { orderId } = useParams<{ orderId: string }>();
  const [formData, setFormData] = useState({
    companyName: receiverInfo.companyName,
    companyEmail: receiverInfo.companyEmail,
    receiverName: receiverInfo.receiverName,
    postalCode: receiverInfo.postalCode || '',
    addressDetail: receiverInfo.addressDetail,
    phoneNumber: receiverInfo.phoneNumber,
  });

  const updateMutation = useApiMutation<
    string,
    Error,
    UpdateShippingAddressRequest
  >((data) => api.patch(`/api/v1/order/address/${orderId}`, data), {
    onSuccess: () => {
      onSuccess();
      onClose(false);
    },
    onError: (error) => {
      alert(extractErrorMessage(error));
    },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.companyName.trim() ||
      !formData.companyEmail.trim() ||
      !formData.receiverName.trim() ||
      !formData.addressDetail.trim() ||
      !formData.phoneNumber.trim()
    ) {
      alert('Please fill in all required fields.');
      return;
    }

    updateMutation.mutate(formData);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-address-modal-title"
    >
      <div className="w-[32.5rem] rounded-lg bg-white text-center">
        <div className="flex w-full items-center justify-between p-6">
          <h3 className="text-xl font-medium" id="edit-address-modal-title">
            Edit Shipping Address
          </h3>
          <button
            type="button"
            className="cursor-pointer bg-transparent"
            onClick={() => onClose(false)}
            aria-label="Close address modal"
            disabled={updateMutation.isPending}
          >
            <X size={20} className="text-[#707070]" aria-hidden="true" />
          </button>
        </div>
        <div className="rounded-b-lg bg-[#f1f1f1] p-6 text-left">
          <form
            className="flex flex-col gap-4"
            aria-labelledby="edit-address-modal-title"
            onSubmit={handleSave}
          >
            <div className="flex items-center justify-between">
              <label htmlFor="companyName" className="text-sm font-normal">
                Company Name
              </label>
              <input
                id="companyName"
                name="companyName"
                type="text"
                placeholder="Company Name"
                value={formData.companyName}
                onChange={handleInputChange}
                className="h-8 w-[22rem] rounded border border-[#cecece] pl-2 text-sm outline-none placeholder:text-[#aaa]"
                aria-label="Company Name"
                required
                disabled={updateMutation.isPending}
              />
            </div>

            <div className="flex items-center justify-between">
              <label htmlFor="companyEmail" className="text-sm font-normal">
                Company Email
              </label>
              <input
                id="companyEmail"
                name="companyEmail"
                type="email"
                placeholder="Company Email"
                value={formData.companyEmail}
                onChange={handleInputChange}
                className="h-8 w-[22rem] rounded border border-[#cecece] pl-2 text-sm outline-none placeholder:text-[#aaa]"
                aria-label="Company Email"
                required
                disabled={updateMutation.isPending}
              />
            </div>

            <div className="flex items-center justify-between">
              <label htmlFor="receiverName" className="text-sm font-normal">
                Receiver Name
              </label>
              <input
                id="receiverName"
                name="receiverName"
                type="text"
                placeholder="Receiver Name"
                value={formData.receiverName}
                onChange={handleInputChange}
                className="h-8 w-[22rem] rounded border border-[#cecece] pl-2 text-sm outline-none placeholder:text-[#aaa]"
                aria-label="Receiver Name"
                required
                disabled={updateMutation.isPending}
              />
            </div>

            <div className="flex items-center justify-between">
              <label htmlFor="phoneNumber" className="text-sm font-normal">
                Phone Number
              </label>
              <input
                id="phoneNumber"
                name="phoneNumber"
                type="tel"
                placeholder="Phone Number"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                className="h-8 w-[22rem] rounded border border-[#cecece] pl-2 text-sm outline-none placeholder:text-[#aaa]"
                aria-label="Phone Number"
                required
                disabled={updateMutation.isPending}
              />
            </div>

            <div className="flex items-center justify-between">
              <label htmlFor="postalCode" className="text-sm font-normal">
                Postal Code
              </label>
              <input
                id="postalCode"
                name="postalCode"
                type="text"
                placeholder="Postal code"
                value={formData.postalCode}
                onChange={handleInputChange}
                className="h-8 w-[22rem] rounded border border-[#cecece] pl-2 text-sm outline-none placeholder:text-[#aaa]"
                aria-label="Postal Code"
                disabled={updateMutation.isPending}
              />
            </div>

            <div className="flex items-center justify-between">
              <label htmlFor="addressDetail" className="text-sm font-normal">
                Address
              </label>
              <input
                id="addressDetail"
                name="addressDetail"
                type="text"
                placeholder="Address"
                value={formData.addressDetail}
                onChange={handleInputChange}
                className="h-8 w-[22rem] rounded border border-[#cecece] pl-2 text-sm outline-none placeholder:text-[#aaa]"
                aria-label="Address"
                required
                disabled={updateMutation.isPending}
              />
            </div>
          </form>
          <div className="mt-6 flex justify-end text-center">
            <button
              type="button"
              onClick={handleSave}
              className="flex h-7 items-center justify-center gap-2 rounded bg-[#3e3e3e] px-3 text-sm font-medium text-white hover:bg-[#222] disabled:cursor-not-allowed disabled:bg-[#888]"
              aria-label="Save address"
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending && (
                <Loader size={14} className="animate-spin" />
              )}
              {updateMutation.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditShippingAddressModal;
